import React, { useCallback, useMemo } from 'react';
import { htmlToSlate, slateToHtml } from 'slate-serializers';

import Underline from './icons/Underline';
import BoldText from './icons/BoldText';
import ItalicText from './icons/ItalicText';
import Strikethrough from './icons/Strikethrough';
import FormatAlignRight from './icons/FormatAlignRight';
import FormatAlignLeft from './icons/FormatAlignLeft';
import FormatAlignCenter from './icons/FormatAlignCenter';
import { slateToHtmlConfig } from './config';
import { withReact, useSlate, Slate } from 'slate-react';
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
} from 'slate';
import { withHistory } from 'slate-history';
import { Button, Toolbar, StyledEditable, Placeholder } from './styles';
const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right'];

const RichTextInput = ({
  htmlContent = `<p style="text-align:left;"></p>`,
  handleChange,
}) => {
  const renderElement = useCallback(
    (props) => <Element {...props} data-test-id={props['data-test-id']} />,
    []
  );

  const renderLeaf = useCallback(
    (props) => <Leaf {...props} data-test-id={props['data-test-id']} />,
    []
  );
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  return (
    <Slate
      editor={editor}
      value={htmlToSlate(htmlContent)}
      onChange={(value) => {
        handleChange(slateToHtml(value, slateToHtmlConfig));
      }}
    >
      <StyledEditable
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        spellCheck
        placeholder="Description..."
        renderPlaceholder={({ attributes, children }) => {
          return <Placeholder {...attributes}>{children}</Placeholder>;
        }}
      />
      <Toolbar>
        <MarkButton format="underline" icon={<Underline />} />
        <MarkButton format="bold" icon={<BoldText />} />
        <MarkButton format="italic" icon={<ItalicText />} />
        <MarkButton format="strikethrough" icon={<Strikethrough />} />
        <BlockButton format="left" icon={<FormatAlignLeft />} />
        <BlockButton format="center" icon={<FormatAlignCenter />} />
        <BlockButton format="right" icon={<FormatAlignRight />} />
      </Toolbar>
    </Slate>
  );
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.strikethrough) {
    children = <del>{children}</del>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, icon }) => {
  const editor = useSlate();

  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
      )}
      onClick={() => {
        toggleBlock(editor, format);
      }}
    >
      {icon}
    </Button>
  );
};

const MarkButton = ({ format, icon }) => {
  const editor = useSlate();

  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      {icon}
    </Button>
  );
};

export default RichTextInput;
